import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Link } from 'react-router-dom';
import {
  Bell,
  Share2,
  Building2,
  Pill,
  Clock,
  Check,
  CheckCheck
} from 'lucide-react';
import { Button } from './ui/button';

const TYPE_CONFIG = {
  case_shared: { icon: Share2, color: '#4B6651' },
  clinic_invite: { icon: Building2, color: '#C06144' },
  treatment_update: { icon: Pill, color: '#4B6651' },
  followup_due: { icon: Clock, color: '#D94F4F' }
};

const NotificationBell = () => {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-[#F8F7F4] transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-[#5C5F5C]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D94F4F] rounded-full flex items-center justify-center text-[10px] text-white font-bold" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-[#E5E4E0] rounded-lg shadow-lg z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-3 border-b border-[#E5E4E0]">
            <span className="text-sm font-semibold text-[#2A2C2A]">Notifications</span>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { markAllRead(); }}
                className="text-xs text-[#4B6651] h-7"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length > 0 ? notifications.map((n) => {
              const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.case_shared;
              const Icon = cfg.icon;
              const patientId = n.data?.patient_id;
              return (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 p-3 border-b border-[#E5E4E0] last:border-0 cursor-pointer hover:bg-[#F8F7F4] transition-colors ${!n.read ? 'bg-[#4B6651]/5' : ''}`}
                  onClick={() => { markRead(n.id); if (patientId) setOpen(false); }}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cfg.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {patientId ? (
                      <Link to={`/cases/${patientId}`} className="text-sm font-medium text-[#2A2C2A] hover:text-[#4B6651] block truncate">
                        {n.subject}
                      </Link>
                    ) : (
                      <p className="text-sm font-medium text-[#2A2C2A] truncate">{n.subject}</p>
                    )}
                    <p className="text-xs text-[#5C5F5C] line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-[#5C5F5C] mt-1">
                      {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                  {!n.read && (
                    <div className="w-2 h-2 rounded-full bg-[#4B6651] mt-2 flex-shrink-0" />
                  )}
                </div>
              );
            }) : (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-[#5C5F5C] mx-auto mb-2 opacity-40" />
                <p className="text-sm text-[#5C5F5C]">No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
